import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getSession } from '@/lib/store';
import { ShieldCheck } from 'lucide-react';

export default function AuthGuard({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthorized, setIsAuthorized] = useState(() => !!getSession());
  const [isLoading, setIsLoading] = useState(() => {
    return !getSession() && location.pathname.startsWith('/dashboard');
  });

  useEffect(() => {
    const session = getSession();
    const TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
    
    // Protection logic
    if (session) {
      const isExpired = session.loginTimestamp && (Date.now() - session.loginTimestamp > TIMEOUT_MS);
      
      if (isExpired) {
        console.warn('Session expired after 30 minutes');
        localStorage.removeItem('charikaty_session'); // Clear session
        navigate('/auth/login', { replace: true });
        return;
      }

      setIsAuthorized(true);
      setIsLoading(false);
    } else if (location.pathname.startsWith('/dashboard')) {
      navigate('/auth/login', { replace: true });
    } else {
      setIsAuthorized(true);
      setIsLoading(false);
    }
  }, [location.pathname, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-bg-primary flex flex-col items-center justify-center gap-8">
         <div className="w-20 h-20 rounded-[2rem] bg-accent-primary/10 border border-accent-primary/30 flex items-center justify-center text-accent-primary animate-pulse">
            <ShieldCheck size={40} />
         </div>
         <div className="flex flex-col items-center gap-2">
            <h2 className="text-[10px] font-bold text-text-muted uppercase ltr:tracking-[0.4em] rtl:tracking-normal">Validation Session</h2>
            <div className="w-48 h-1 bg-bg-secondary relative overflow-hidden rounded-full border border-border">
               <div className="absolute inset-y-0 h-full w-24 bg-accent-primary shadow-[0_0_15px_rgba(var(--accent-primary),0.5)] animate-shimmer" />
            </div>
         </div>
      </div>
    );
  }

  return <>{isAuthorized ? children : null}</>;
}
